// frontend/admin-portal/src/services/fileService.js
import axios from 'axios';

const FILE_API =
    process.env.REACT_APP_FILE_API || 'http://localhost:5002/api';
const FILE_BASE =
    process.env.REACT_APP_FILE_BASE || 'http://localhost:5002';


// Dev-time check — helps diagnose misconfiguration
if (process.env.NODE_ENV === 'development') {
    // eslint-disable-next-line no-console
    console.info('[fileService] FILE_API =', FILE_API);
    // eslint-disable-next-line no-console
    console.info('[fileService] FILE_BASE =', FILE_BASE);
}

const fileApi = axios.create({
    baseURL: FILE_API,
    timeout: 60000,
});

fileApi.interceptors.request.use((config) => {
    const token = localStorage.getItem('accessToken');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
});

export { FILE_BASE };

export const fileService = {
    uploadImage: async (file, { entityType = 'generic', entityId = null, onProgress } = {}) => {
        const form = new FormData();
        form.append('file', file);
        form.append('entityType', entityType);
        if (entityId) form.append('entityId', String(entityId));

        const { data } = await fileApi.post('/images', form, {
            headers: { 'Content-Type': 'multipart/form-data' },
            onUploadProgress: (e) => {
                if (onProgress && e.total) {
                    onProgress(Math.round((e.loaded / e.total) * 100));
                }
            },
        });
        return data.data.file;
    },

    uploadImages: async (files, { entityType = 'generic', entityId = null, onProgress } = {}) => {
        const form = new FormData();
        Array.from(files).forEach((f) => form.append('files', f));
        form.append('entityType', entityType);
        if (entityId) form.append('entityId', String(entityId));

        const { data } = await fileApi.post('/images/bulk', form, {
            headers: { 'Content-Type': 'multipart/form-data' },
            onUploadProgress: (e) => {
                if (onProgress && e.total) {
                    onProgress(Math.round((e.loaded / e.total) * 100));
                }
            },
        });
        return data.data.files;
    },

    uploadDocument: async (file, { entityType = 'generic', entityId = null, onProgress } = {}) => {
        const form = new FormData();
        form.append('file', file);
        form.append('entityType', entityType);
        if (entityId) form.append('entityId', String(entityId));

        const { data } = await fileApi.post('/documents', form, {
            headers: { 'Content-Type': 'multipart/form-data' },
            onUploadProgress: (e) => {
                if (onProgress && e.total) {
                    onProgress(Math.round((e.loaded / e.total) * 100));
                }
            },
        });
        return data.data.file;
    },

    list: async (params = {}) => {
        const { data } = await fileApi.get('/', { params });
        return data.data;
    },

    get: async (idOrUuid) => {
        const { data } = await fileApi.get(`/${idOrUuid}`);
        return data.data.file;
    },

    remove: async (idOrUuid) => {
        const { data } = await fileApi.delete(`/${idOrUuid}`);
        return data;
    },

    attach: async (id, { entityType, entityId }) => {
        const { data } = await fileApi.patch(`/${id}/attach`, { entityType, entityId });
        return data.data.file;
    },

    previewImport: async (file) => {
        const form = new FormData();
        form.append('file', file);
        const { data } = await fileApi.post('/import-export/preview', form, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return data.data;
    },

    exportCSV: async ({ filename = 'export', headers, rows }) => {
        const res = await fileApi.post(
            '/import-export/export/csv',
            { filename, headers, rows },
            { responseType: 'blob' }
        );
        const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${filename}-${Date.now()}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    },

    removeByUrl: async (url) => {
        if (!url) return null;
        const { data } = await fileApi.delete('/by-url', { params: { url } });
        return data;
    },
};

/**
 * Resolve any image entry to an absolute URL.
 * Handles:
 *   - Absolute URLs (http/https) → returned as-is
 *   - Relative paths (/uploads/...) → prefixed with FILE_BASE
 *   - Full URLs containing the base → returned as-is
 *   - Empty/null/undefined → returns ''
 */
export function resolveImageUrl(img) {
    if (!img) return '';

    // Handle object shapes (e.g. FileRecord returned from backend)
    if (typeof img === 'object') {
        if (img.url) return resolveImageUrl(img.url);
        if (img.thumbnailUrl) return resolveImageUrl(img.thumbnailUrl);
        return '';
    }

    const str = String(img).trim();
    if (!str) return '';

    // Already an absolute URL
    if (/^https?:\/\//i.test(str)) return str;

    // It's a relative path — prefix with the file-service base
    return `${FILE_BASE}${str.startsWith('/') ? '' : '/'}${str}`;
}

/** Reverse: strip the FILE_BASE prefix to get the relative path (for older records) */
export function toRelativeUploadPath(url) {
    if (!url) return '';
    return String(url)
        .replace(/^https?:\/\/[^/]+/, '')
        .replace(/^\/uploads\//, '')
        .replace(/^\//, '');
}